import { useEffect, useMemo } from 'react';
import type {
  WizardStepProps,
  TournamentConfigSlice,
} from '../../../engine/types';
import {
  SignupDeadlineField,
  MatchDurationField,
  MinSchedulesRequiredField,
  CountsForCategoryRankingField,
  TournamentConfigHeader,
  TournamentConfigSummaryCard,
  DEFAULT_TOURNAMENT_CONFIG_FIELD_CONFIG,
  type TournamentConfigFieldConfig,
} from '../../../blocks/tournament-config';
import { LongConfigBlock, longConfigSchema } from '../../../blocks/long-config';
import type { LongConfigValues } from '../../../blocks/long-config';
import { TournamentFormat } from '@/types/tournament.model';
import { defaultCountsForCategoryRanking, isAmericanFormat } from '@/utils/tournament-format';
import { useEloCategories } from '@/hooks/useEloCategories';
import { CATEGORY_RANKING_TARGET } from '@/types/tournament.model';

export type { TournamentConfigFieldConfig } from '../../../blocks/tournament-config';

const DEFAULT_CONFIG: TournamentConfigFieldConfig =
  DEFAULT_TOURNAMENT_CONFIG_FIELD_CONFIG;

interface TournamentConfigStepProps extends WizardStepProps<TournamentConfigSlice> {
  config?: TournamentConfigFieldConfig;
}

export function TournamentConfigStep({
  data,
  draft,
  updateData,
  config: userConfig,
}: TournamentConfigStepProps) {
  const config = { ...DEFAULT_CONFIG, ...userConfig };

  const isAmerican = isAmericanFormat(draft.fixtureConfig?.format);

  const rawSignupDeadline = data?.signupDeadlineDate ?? null;
  const signupDeadlineDate =
    rawSignupDeadline == null
      ? null
      : rawSignupDeadline instanceof Date
        ? rawSignupDeadline
        : new Date(rawSignupDeadline as unknown as string);
  const matchDuration =
    data?.matchDuration ?? config.matchDuration?.default ?? 60;

  // For Americano, minSchedulesRequired is always null (not applicable).
  const minSchedulesRequired = isAmerican
    ? null
    : (data?.minSchedulesRequired ?? config.minSchedulesRequired?.default ?? 1);

  const countsForCategoryRanking =
    data?.countsForCategoryRanking ?? defaultCountsForCategoryRanking(draft.fixtureConfig?.format);
  const categoryRankingTarget =
    data?.categoryRankingTarget ?? CATEGORY_RANKING_TARGET.PLAYER_CATEGORY;

  const allCategories = useEloCategories(draft.sport?.sport?.uid);
  const rangeCategories = useMemo(() => {
    const min = draft.teamConfig?.minCategory?.minimumElo;
    const max = draft.teamConfig?.maxCategory?.minimumElo;
    if (min === undefined || max === undefined) return [];
    const low = Math.min(min, max);
    const high = Math.max(min, max);
    return allCategories.filter(
      (category) => category.minimumElo >= low && category.minimumElo <= high,
    );
  }, [allCategories, draft.teamConfig?.minCategory, draft.teamConfig?.maxCategory]);

  const minDur = config.matchDuration?.min ?? 30;

  const isLong = draft?.fixtureConfig?.format === TournamentFormat.LONG;

  const categoryRankingCategoryId = data?.categoryRankingCategory?.id ?? null;
  const needsFixedCategory =
    countsForCategoryRanking &&
    categoryRankingTarget === CATEGORY_RANKING_TARGET.FIXED_CATEGORY;
  // The backend requires a target category inside the tournament's range for
  // FIXED_CATEGORY, so an unset or out-of-range one keeps the step invalid.
  const hasValidFixedCategory =
    !needsFixedCategory ||
    rangeCategories.some(
      (category) => category.id === categoryRankingCategoryId,
    );

  // LONG advertises the weekly-config fields as required, so the step gate has to enforce
  // them too: otherwise the wizard submits nulls and the round lifecycle never runs.
  const isLongConfigValid =
    !isLong ||
    longConfigSchema.safeParse({
      availabilityDeadlineDay: data?.availabilityDeadlineDay,
      availabilityDeadlineTime: data?.availabilityDeadlineTime,
      minSchedulesRequired: data?.minSchedulesRequired,
      walkoversForDisqualification: data?.walkoversForDisqualification,
      autoPublishRounds: data?.autoPublishRounds ?? false,
    }).success;

  const isValid = Boolean(
    signupDeadlineDate && matchDuration >= minDur && hasValidFixedCategory && isLongConfigValid,
  );

  useEffect(() => {
    if (!data) {
      updateData({
        signupDeadlineDate: null,
        matchDuration: isAmerican ? 30 : (config.matchDuration?.default ?? 60),
        // Americano: send null; other formats: use configured default.
        minSchedulesRequired: isAmerican
          ? null
          : (config.minSchedulesRequired?.default ?? 1),
        countsForCategoryRanking: defaultCountsForCategoryRanking(draft.fixtureConfig?.format),
        categoryRankingTarget: CATEGORY_RANKING_TARGET.PLAYER_CATEGORY,
        categoryRankingCategory: null,
        isValid: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, updateData, config.minSchedulesRequired?.default, isAmerican]);

  // Normalize any existing non-null value to null when the format is Americano.
  // Prevents an orphaned value from persisting in the localStorage draft.
  useEffect(() => {
    if (
      isAmerican &&
      data?.minSchedulesRequired !== null &&
      data?.minSchedulesRequired !== undefined
    ) {
      updateData({ minSchedulesRequired: null });
    }
  }, [isAmerican, data?.minSchedulesRequired, updateData]);

  useEffect(() => {
    if (!data?.signupDeadlineDate) return;

    if (!(data.signupDeadlineDate instanceof Date)) {
      const parsed = new Date(data.signupDeadlineDate as unknown as string);
      if (!Number.isNaN(parsed.getTime())) {
        updateData({ signupDeadlineDate: parsed });
      } else {
        updateData({ signupDeadlineDate: null });
      }
    }
  }, [data?.signupDeadlineDate, updateData]);

  // The category range is picked in an earlier step: narrowing it there can leave the
  // target category outside the range. Re-pick the first one in range instead of
  // sending a category the backend will reject.
  useEffect(() => {
    if (!needsFixedCategory || rangeCategories.length === 0) return;
    if (
      rangeCategories.some(
        (category) => category.id === categoryRankingCategoryId,
      )
    ) {
      return;
    }
    updateData({ categoryRankingCategory: rangeCategories[0] });
  }, [
    needsFixedCategory,
    rangeCategories,
    categoryRankingCategoryId,
    updateData,
  ]);

  useEffect(() => {
    if (data?.isValid !== isValid) {
      updateData({ isValid });
    }
  }, [isValid, data?.isValid, updateData]);

  const handleSignupDeadlineChange = (date: Date | null) => {
    updateData({ signupDeadlineDate: date });
  };

  const handleMatchDurationChange = (value: number) => {
    updateData({ matchDuration: value });
  };

  const handleMinSchedulesRequiredChange = (value: number) => {
    updateData({ minSchedulesRequired: value });
  };

  // A LONG weekly grid shared with other tournaments dictates the availability deadline.
  const gridConfig = draft.clubSchedules?.weeklyGridConfig;
  const lockedDeadline =
    gridConfig?.availabilityDeadlineDay && gridConfig.availabilityDeadlineTime
      ? {
          day: gridConfig.availabilityDeadlineDay as LongConfigValues['availabilityDeadlineDay'],
          time: gridConfig.availabilityDeadlineTime,
        }
      : null;

  return (
    <div className="space-y-6">
      <TournamentConfigHeader />

      <SignupDeadlineField
        value={signupDeadlineDate}
        onChange={handleSignupDeadlineChange}
        config={config}
      />

      <MatchDurationField
        value={matchDuration}
        onChange={handleMatchDurationChange}
        config={config}
      />

      {/* minSchedulesRequired standalone: non-American, non-LONG. For LONG it lives inside LongConfigBlock. */}
      {!isAmerican && !isLong && (
        <MinSchedulesRequiredField
          value={minSchedulesRequired ?? 1}
          onChange={handleMinSchedulesRequiredChange}
          config={config}
        />
      )}

      {/* Weekly config: LONG tournaments only */}
      {isLong && (
        <LongConfigBlock
          values={{
            availabilityDeadlineDay: (data?.availabilityDeadlineDay as LongConfigValues['availabilityDeadlineDay']) ?? undefined,
            availabilityDeadlineTime: data?.availabilityDeadlineTime ?? undefined,
            minSchedulesRequired: data?.minSchedulesRequired ?? undefined,
            walkoversForDisqualification: data?.walkoversForDisqualification ?? undefined,
            autoPublishRounds: data?.autoPublishRounds ?? false,
          }}
          onChange={(patch) => updateData(patch as Partial<TournamentConfigSlice>)}
          lockedDeadline={lockedDeadline}
        />
      )}
      <CountsForCategoryRankingField
        value={countsForCategoryRanking}
        onChange={(next) => updateData({ countsForCategoryRanking: next })}
        categories={rangeCategories}
        target={categoryRankingTarget}
        onTargetChange={(next) =>
          updateData({
            categoryRankingTarget: next,
            categoryRankingCategory:
              next === CATEGORY_RANKING_TARGET.FIXED_CATEGORY
                ? (data?.categoryRankingCategory ?? rangeCategories[0] ?? null)
                : null,
          })
        }
        categoryId={data?.categoryRankingCategory?.id ?? null}
        onCategoryChange={(next) =>
          updateData({
            categoryRankingCategory:
              rangeCategories.find((category) => category.id === next) ?? null,
          })
        }
      />

      {isValid && signupDeadlineDate && (
        <TournamentConfigSummaryCard
          signupDeadlineDate={signupDeadlineDate}
          matchDuration={matchDuration}
          minSchedulesRequired={minSchedulesRequired}
          showMinSchedules={!isAmerican}
          countsForCategoryRanking={countsForCategoryRanking}
          categoryRankingTargetLabel={
            categoryRankingTarget === CATEGORY_RANKING_TARGET.FIXED_CATEGORY
              ? (data?.categoryRankingCategory?.name ?? 'categoría fija')
              : 'categoría del jugador'
          }
        />
      )}
    </div>
  );
}

export default TournamentConfigStep;
